import { useQuery } from '@tanstack/react-query';
import { User, Users } from 'lucide-react';
import { Link } from 'react-router';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableTopHeader,
  TableWrapper,
} from '@/components/deprecated/OldTable';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { MEDIA_URL } from '@/config';
import { Matchdata } from '@/types/match';

import { fetchScoreboard } from '../api/getScoreboard';

type Props = {
  match?: Matchdata;
  matchId: string;
  mapNumber?: number;
};

const Scoreboard = (props: Props) => {
  const { data: matchScoreboard } = useQuery({
    queryKey: ['matchScoreboard', props.matchId, props.mapNumber],
    queryFn: () => fetchScoreboard(props.matchId, props.mapNumber),
  });
  return (
    <>
      <TableWrapper>
        <TableTopHeader
          title={
            <div className="flex items-center gap-x-2">
              {props.match ? (
                <>
                  <Avatar className="h-9 w-9">
                    <AvatarImage
                      src={`${MEDIA_URL}/images/uploads/${props.match.team1.team_photo}`}
                      alt={props.match.team1.team_name || ''}
                    />
                    <AvatarFallback className="bg-secondary">
                      <Users />
                    </AvatarFallback>
                  </Avatar>
                  {props.match.team1.team_name}
                </>
              ) : (
                <>
                  <Skeleton className="h-9 w-9 rounded-full!" />
                  <Skeleton className="h-3.5 w-48" />
                </>
              )}
            </div>
          }
        ></TableTopHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">
                <span className="uppercase tracking-wide text-muted-foreground">
                  Name
                </span>
              </TableHead>
              <TableHead>
                <span className="uppercase tracking-wide text-muted-foreground">
                  Kills
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  Assists
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  Deaths
                </span>
              </TableHead>
              <TableHead className="">
                <span className="whitespace-nowrap uppercase tracking-wide text-muted-foreground">
                  K/R
                </span>
              </TableHead>
              <TableHead className="">
                <span className="whitespace-nowrap uppercase tracking-wide text-muted-foreground">
                  K/D
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  HS
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  MVP(s)
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  3k
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  4k
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  5k
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercse tracking-wide text-muted-foreground">
                  Entry
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  Entry success
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!matchScoreboard?.team1 ? (
              <>
                {Array.from({ length: 5 }, (_, index) => (
                  <TableRow key={index}>
                    <TableCell className="flex items-center gap-2 ">
                      <Skeleton className="h-6 w-6 rounded-full!" />
                      <Skeleton className="h-2.5 w-full" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : (
              <>
                {matchScoreboard.team1.map((player, index) => (
                  <TableRow
                    key={player.steamid64 + 's' + props.mapNumber + index}
                  >
                    <TableCell className="flex items-center gap-2 ">
                      <Avatar className="h-6 w-6 text-[8px]">
                        <AvatarImage
                          src={`${
                            player.user.profile_photo.includes('https')
                              ? player.user.profile_photo
                              : `${MEDIA_URL}/${player.user.profile_photo}`
                          }`}
                          alt={player.user.username}
                        />
                        <AvatarFallback className="bg-secondary">
                          <User />
                        </AvatarFallback>
                      </Avatar>
                      <Link to={`/profile/${player.steamid64}`}>
                        {player.user.username}
                      </Link>
                    </TableCell>
                    <TableCell className="">{player.kills}</TableCell>
                    <TableCell className="">{player.assists}</TableCell>
                    <TableCell className="">{player.deaths}</TableCell>
                    <TableCell className="">{player.killsPerRound}</TableCell>
                    <TableCell className="">{player.killDeathRatio}</TableCell>
                    <TableCell className="">{player.headshots}</TableCell>
                    <TableCell className="">{player.mvp}</TableCell>
                    <TableCell className="">{player['3k']}</TableCell>
                    <TableCell className="">{player['4k']}</TableCell>
                    <TableCell className="">{player['5k']}</TableCell>
                    <TableCell className="">{player.entries}</TableCell>
                    <TableCell className="">
                      {player.entrySuccessRate}
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
      <TableWrapper>
        <TableTopHeader
          title={
            <div className="flex items-center gap-x-2">
              {props.match ? (
                <>
                  <Avatar className="h-9 w-9">
                    <AvatarImage
                      src={`${MEDIA_URL}/images/uploads/${props.match.team2.team_photo}`}
                      alt={props.match.team2.team_name || ''}
                    />
                    <AvatarFallback className="bg-secondary">
                      <User />
                    </AvatarFallback>
                  </Avatar>
                  {props.match.team2.team_name}
                </>
              ) : (
                <>
                  <Skeleton className="h-9 w-9 rounded-full!" />
                  <Skeleton className="h-3.5 w-48" />
                </>
              )}
            </div>
          }
        ></TableTopHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-44">
                <span className="uppercase tracking-wide text-muted-foreground">
                  Name
                </span>
              </TableHead>
              <TableHead>
                <span className="uppercase tracking-wide text-muted-foreground">
                  Kills
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  Assists
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  Deaths
                </span>
              </TableHead>
              <TableHead className="">
                <span className="whitespace-nowrap uppercase tracking-wide text-muted-foreground">
                  K/R
                </span>
              </TableHead>
              <TableHead className="">
                <span className="whitespace-nowrap uppercase tracking-wide text-muted-foreground">
                  K/D
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  HS
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  MVP(s)
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  3k
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  4k
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  5k
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercse tracking-wide text-muted-foreground">
                  Entry
                </span>
              </TableHead>
              <TableHead className="">
                <span className="uppercase tracking-wide text-muted-foreground">
                  Entry success
                </span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!matchScoreboard?.team1 ? (
              <>
                {Array.from({ length: 5 }, (_, index) => (
                  <TableRow key={index}>
                    <TableCell className="flex items-center gap-2 ">
                      <Skeleton className="h-6 w-6 rounded-full!" />
                      <Skeleton className="h-2.5 w-full" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                    <TableCell className="text-center">
                      <Skeleton className="mx-auto h-2.5 w-auto" />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : (
              <>
                {matchScoreboard.team2.map((player, index) => (
                  <TableRow
                    key={player.steamid64 + 's' + props.mapNumber + index}
                  >
                    <TableCell className="flex items-center gap-2 ">
                      <Avatar className="h-6 w-6 text-[8px]">
                        <AvatarImage
                          src={`${
                            player.user.profile_photo.includes('https')
                              ? player.user.profile_photo
                              : `${MEDIA_URL}/${player.user.profile_photo}`
                          }`}
                          alt={player.user.username}
                        />
                        <AvatarFallback className="bg-secondary">
                          <User />
                        </AvatarFallback>
                      </Avatar>
                      <Link to={`/profile/${player.steamid64}`}>
                        {player.user.username}
                      </Link>
                    </TableCell>
                    <TableCell className="">{player.kills}</TableCell>
                    <TableCell className="">{player.assists}</TableCell>
                    <TableCell className="">{player.deaths}</TableCell>
                    <TableCell className="">{player.killsPerRound}</TableCell>
                    <TableCell className="">{player.killDeathRatio}</TableCell>
                    <TableCell className="">{player.headshots}</TableCell>
                    <TableCell className="">{player.mvp}</TableCell>
                    <TableCell className="">{player['3k']}</TableCell>
                    <TableCell className="">{player['4k']}</TableCell>
                    <TableCell className="">{player['5k']}</TableCell>
                    <TableCell className="">{player.entries}</TableCell>
                    <TableCell className="">
                      {player.entrySuccessRate}
                    </TableCell>
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </>
  );
};

export default Scoreboard;
